import axios from 'axios';
import { getApiUrl } from '../config/api';

// Service to handle iframe entry messages and create calendar events
export class IframeEntryService {
  constructor() {
    this.isListening = false;
    this.messageHandler = null;
  }

  // Start listening for iframe messages
  startListening() {
    if (this.isListening) {
      return;
    }

    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this.messageHandler);
    this.isListening = true;
    
  }

  // Stop listening for iframe messages
  stopListening() {
    if (!this.isListening) {
      return;
    }

    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    
    this.isListening = false;
  }


  // Handle incoming iframe messages
  async handleMessage(event) {
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    try {
      // Only handle messages from the expected domain; silently ignore others (e.g., Stripe)
      if (event.origin !== 'https://entry.kcirodeo.net') {
        return;
      }

      console.log(`📨 [${messageId}] Received iframe message from origin: ${event.origin}`);
      console.log(`📋 [${messageId}] Message data:`, event.data);

      // Check if this is an entry completion message
      if (event.data && event.data.type === 'ENTRY_COMPLETED') {
        console.log(`✅ [${messageId}] Received entry completion message`);
        
        const { confirmationNumber, associationKey, memberKey, memberPass } = event.data;
        
        if (!confirmationNumber) {
          console.error(`❌ [${messageId}] No confirmation number in entry message`);
          return;
        }

        console.log(`🚀 [${messageId}] Processing entry with confirmation: ${confirmationNumber.slice(-4)}`);
        // Process the entry and create calendar event
        await this.processEntryAndCreateEvent(confirmationNumber, { associationKey, memberKey, memberPass });
      } else {
        console.log(`ℹ️ [${messageId}] Message type not recognized:`, event.data?.type || 'unknown');
      }
    } catch (error) {
      console.error(`❌ [${messageId}] Error handling iframe message:`, error);
    }
  }

  // Process entry by confirmation number and create calendar event
  async processEntryAndCreateEvent(confirmationNumber, providedCredentials = null) {
    const processId = `proc_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    console.log(`🔄 [${processId}] Starting entry processing for confirmation: ${confirmationNumber.slice(-4)}`);
    
    try {

      let associationKey, memberKey, memberPass, showId;

      if (providedCredentials && providedCredentials.associationKey) {
        // Use provided credentials from the message
        console.log(`🔑 [${processId}] Using provided credentials from message`);
        associationKey = providedCredentials.associationKey;
        memberKey = providedCredentials.memberKey || '';
        memberPass = providedCredentials.memberPass || '';
        showId = providedCredentials.showId || null;
      } else if (typeof sessionStorage !== 'undefined') {
        // Try to get association data from sessionStorage (set when iframe was opened)
        try {
          const storedAssociation = sessionStorage.getItem('currentIframeAssociation');
          if (storedAssociation) {
            const associationData = JSON.parse(storedAssociation);
            console.log(`💾 [${processId}] Using stored association data from sessionStorage: ${associationData.associationKey?.slice(-4)}`);
            associationKey = associationData.associationKey;
            memberKey = associationData.memberKey || '';
            memberPass = associationData.memberPass || '';
            showId = associationData.showId || null;
          } else {
            console.log(`⚠️ [${processId}] No stored association data found in sessionStorage`);
          }
        } catch (error) {
          console.warn(`⚠️ [${processId}] Could not parse stored association data:`, error);
        }
      }
      
      if (!associationKey) {
        // Fallback: Get user associations for credentials
        console.log(`🔄 [${processId}] Using fallback: getting associations from API`);
        const associationsResponse = await axios.get(getApiUrl('/api/user-associations'));
        if (associationsResponse.status !== 200) {
          throw new Error('Failed to load user associations');
        }

        const associations = associationsResponse.data;
        if (!associations || associations.length === 0) {
          throw new Error('No associations configured. Please set up your association credentials in Settings.');
        }

        // Use the first association as fallback
        const association = associations[0];
        console.log(`🔑 [${processId}] Using first association as fallback: ${association?.associationName?.slice(-4)}`);
        
        if (!association || !association.associationName) {
          throw new Error('Association name not configured. Please check your association settings.');
        }
        
        associationKey = association.associationName;
        memberKey = association.userId || '';
        memberPass = association.userPassword || '';
      }

      console.log(`🚀 [${processId}] Making API call to process entry with credentials:`, {
        associationKey: associationKey?.slice(-4),
        memberKey: memberKey?.slice(-4),
        confirmationNumber: confirmationNumber.slice(-4)
      });

      // Get entry details from SQL Server
      const apiStartTime = Date.now();
      const entryResponse = await axios.post(getApiUrl('/api/sqlserver-entries/get-by-confirmation'), {
        confirmationNumber,
        associationKey,
        memberKey,
        memberPass,
        showId  // Include showId for the stored procedure
      });
      const apiDuration = Date.now() - apiStartTime;

      console.log(`✅ [${processId}] API call completed in ${apiDuration}ms with status: ${entryResponse.status}`);

      if (entryResponse.status !== 200) {
        throw new Error('Failed to get entry details');
      }

      const entryData = entryResponse.data;
      console.log(`📊 [${processId}] Entry data retrieved:`, {
        success: entryData.success,
        count: entryData.count,
        entriesCount: entryData.entries?.length || 0
      });
      console.log(`📊 [${processId}] FULL Entry data:`, JSON.stringify(entryData, null, 2));

      if (!entryData.entries || entryData.entries.length === 0) {
        throw new Error('No entries found in response');
      }

      // Transform entries (add source field) - same as Import Entries button
      console.log(`🔄 [${processId}] Transforming entries for import`);
      const transformedEntries = entryData.entries.map(entry => ({
        ...entry,
        source: 'kcirodeo'
      }));

      // Save using the SAME endpoint as Import Entries button
      console.log(`💾 [${processId}] Saving ${transformedEntries.length} entries via /api/entries/import`);
      const saveResponse = await axios.post(getApiUrl('/api/entries/import'), {
        entries: transformedEntries,
        familyMemberId: null  // No family member for iframe imports
      });

      const saveResult = saveResponse.data;
      console.log(`✅ [${processId}] Save completed:`, saveResult.summary);

      // Show success notification
      console.log(`🎉 [${processId}] Entry processing completed successfully`);
      this.showSuccessNotification(saveResult);
      
    } catch (error) {
      console.error(`❌ [${processId}] Error processing entry:`, error);
      console.error(`❌ [${processId}] Error details:`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        requestId: error.response?.data?.requestId
      });
      this.showErrorNotification(error.message);
    }
  }


  // Show success notification
  showSuccessNotification(saveResult) {
    // Dispatch custom event for notification system
    const summary = saveResult.summary || {};
    const { total = 0, imported = 0, errors = 0, calendarEventsCreated = 0 } = summary;

    let message = saveResult.message || 'Entry processed successfully!';

    // Format message same as Import Entries button
    if (imported > 0 && errors > 0) {
      message = `Successfully processed ${total} entries (${imported} imported, ${errors} errors)`;
    } else if (imported > 0) {
      message = `Successfully imported ${imported} entries`;
    } else if (errors > 0) {
      message = `Failed to import entries: ${errors} errors`;
    }

    // Add calendar events info
    if (calendarEventsCreated > 0) {
      message += ` and created ${calendarEventsCreated} calendar events`;
    }

    const event = new CustomEvent('entry-processed', {
      detail: {
        type: 'success',
        message: message,
        entryData: saveResult
      }
    });
    window.dispatchEvent(event);
  }

  // Show error notification
  showErrorNotification(errorMessage) {
    // Dispatch custom event for notification system
    const event = new CustomEvent('entry-processed', {
      detail: {
        type: 'error',
        message: `Failed to process entry: ${errorMessage}`,
        error: errorMessage
      }
    });
    window.dispatchEvent(event);
  }
}

// Create singleton instance
export const iframeEntryService = new IframeEntryService();

